import React from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-icons/tb';

import { useTranslation } from '../../../translations';
import { SignaturePositionSchemaDto } from '../../../common/pokCore/autogenerated/pokApiClient';

export interface SignaturePositionRow {
  position: SignaturePositionSchemaDto;
  draft: boolean;
}

interface SignaturePositionsPanelProps {
  rows: SignaturePositionRow[];
  numPages: number;
  editable: boolean;
  editing: boolean;
  saving: boolean;
  addDisabledReason?: string;
  deleteLastBlocked: boolean;
  highlightedIndex?: number;
  onHighlight: (index?: number) => void;
  onAdd: () => void;
  onMove: (index: number) => void;
  onDelete: (index: number) => void;
  onGoToPage: (page: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const WithTooltip: React.FC<{
  text?: string;
  children: React.ReactElement;
}> = ({ text, children }) =>
  text ? (
    <OverlayTrigger placement="bottom" overlay={<Tooltip>{text}</Tooltip>}>
      <span className="d-inline-block">{children}</span>
    </OverlayTrigger>
  ) : (
    children
  );

export const SignaturePositionsPanel: React.FC<
  SignaturePositionsPanelProps
> = props => {
  const { t, tk } = useTranslation('commonLng');
  const confirmedCount = props.rows.filter(row => !row.draft).length;

  const deleteBlockedReason = props.deleteLastBlocked
    ? t(tk.pdfViewer.lastSignatureRequired)
    : undefined;

  return (
    <div className="signature-panel">
      <div className="signature-panel__header">
        <strong>
          {t(tk.pdfViewer.signatureAdded, { count: confirmedCount })}
        </strong>
        {props.editable && (
          <WithTooltip text={props.addDisabledReason}>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={props.onAdd}
              disabled={
                !!props.addDisabledReason || props.editing || props.saving
              }
            >
              <Icon.TbPlus size={16} /> {t(tk.pdfViewer.addSignature)}
            </Button>
          </WithTooltip>
        )}
      </div>

      {props.rows.length === 0 && props.editable && (
        <div className="text-danger  mt-2">
          {t(tk.pdfViewer.noSignaturePositions)}
        </div>
      )}

      <ul className="signature-panel__list">
        {props.rows.map((row, index) => {
          const pageMissing = row.position.page > props.numPages;
          const rowEditing = props.editing && !row.draft;
          return (
            <li
              key={index}
              className={`signature-panel__row${
                props.highlightedIndex === index
                  ? ' signature-panel__row--highlighted'
                  : ''
              }`}
              onMouseEnter={() => props.onHighlight(index)}
              onMouseLeave={() => props.onHighlight(undefined)}
            >
              <span className="signature-panel__number">{index + 1}</span>
              <span>
                {t(tk.pdfViewer.signatureNumber, { number: index + 1 })}
              </span>
              <span className={pageMissing ? 'text-danger' : 'text-muted'}>
                {pageMissing
                  ? t(tk.pdfViewer.signaturePageMissing, {
                      page: row.position.page,
                    })
                  : t(tk.pdfViewer.signaturePage, { page: row.position.page })}
              </span>
              {row.draft ? (
                <span className="text-danger text-uppercase small">
                  {t(tk.pdfViewer.notConfirmed)}
                </span>
              ) : (
                <WithTooltip text={t(tk.pdfViewer.confirmed)}>
                  <Icon.TbCircleCheckFilled
                    className="text-success"
                    size={18}
                  />
                </WithTooltip>
              )}

              <span className="signature-panel__actions">
                {row.draft ? (
                  <>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={props.onConfirm}
                      disabled={props.saving}
                    >
                      <Icon.TbCheck size={16} />{' '}
                      {t(tk.pdfViewer.confirmSignature)}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={props.onCancel}
                      disabled={props.saving}
                    >
                      <Icon.TbX size={16} /> {t(tk.pdfViewer.cancelSignature)}
                    </Button>
                  </>
                ) : (
                  <>
                    {!pageMissing && (
                      <WithTooltip text={t(tk.pdfViewer.goToSignature)}>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => props.onGoToPage(row.position.page)}
                          disabled={props.editing}
                        >
                          <Icon.TbArrowRight size={16} />
                        </Button>
                      </WithTooltip>
                    )}
                    {props.editable && (
                      <>
                        <WithTooltip text={t(tk.pdfViewer.moveSignature)}>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => props.onMove(index)}
                            disabled={rowEditing || props.saving}
                          >
                            <Icon.TbArrowsMove size={16} />
                          </Button>
                        </WithTooltip>
                        <WithTooltip
                          text={
                            deleteBlockedReason ??
                            t(tk.pdfViewer.deleteSignature)
                          }
                        >
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => props.onDelete(index)}
                            disabled={
                              rowEditing ||
                              props.saving ||
                              !!deleteBlockedReason
                            }
                          >
                            <Icon.TbTrash size={16} />
                          </Button>
                        </WithTooltip>
                      </>
                    )}
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {props.editable && (
        <ol className="signature-panel__help small text-muted">
          <li>{t(tk.pdfViewer.signatureHelpAdd)}</li>
          <li>{t(tk.pdfViewer.signatureHelpDrag)}</li>
          <li>{t(tk.pdfViewer.signatureHelpConfirm)}</li>
        </ol>
      )}
    </div>
  );
};
